/**
 * A clock as data, so a recorded take can rebuild exactly the clock it was graded on.
 *
 * A take's clock is a tempo map: one or more segments, each anchored at a session time and at
 * the beat the clock had reached there. A set tempo is a map of one segment. A player's own tempo
 * change inside a take appends a segment at the moment of the change; the take keeps going and is
 * stamped at the slowest tempo it used.
 */

import { Clock } from '../clock';
import { GradingConfigError } from '../types';

export interface TempoSegment {
  /** Session time (ms) at which this tempo takes over. */
  readonly atMs: number;
  /** The beat the clock had reached at `atMs`. */
  readonly atBeat: number;
  readonly bpm: number;
}

export interface ClockSpec {
  readonly kind: 'tempo-map';
  /** In time order; the first anchors the clock and also runs backwards from its anchor. */
  readonly segments: readonly TempoSegment[];
}

const MS_PER_MINUTE = 60000;

function isTempo(bpm: number): boolean {
  return Number.isFinite(bpm) && bpm > 0;
}

/** Every validity problem with a clock spec; empty when valid. */
export function clockSpecErrors(spec: ClockSpec): string[] {
  if (spec.kind !== 'tempo-map' || spec.segments.length === 0) {
    return ['clock: a tempo map needs at least one segment'];
  }
  const badSegment = spec.segments.some(
    (segment) =>
      !isTempo(segment.bpm) || !Number.isFinite(segment.atMs) || !Number.isFinite(segment.atBeat),
  );
  const outOfOrder = spec.segments.some(
    (segment, index) =>
      index > 0 &&
      !(
        segment.atMs > spec.segments[index - 1].atMs &&
        segment.atBeat >= spec.segments[index - 1].atBeat
      ),
  );
  return [
    ...(badSegment ? ['clock: every segment needs a positive tempo and finite anchors'] : []),
    ...(outOfOrder ? ['clock: segments must move forward in time and in beats'] : []),
  ];
}

/** A set tempo: beat 0 falls due at `startMs`, as `constantTempoClock` does. */
export function setTempoSpec(bpm: number, startMs = 0): ClockSpec {
  return { kind: 'tempo-map', segments: [{ atMs: startMs, atBeat: 0, bpm }] };
}

function segmentForBeat(spec: ClockSpec, beat: number): TempoSegment {
  return spec.segments.reduce(
    (found, segment) => (segment.atBeat <= beat ? segment : found),
    spec.segments[0],
  );
}

function segmentForTime(spec: ClockSpec, timeMs: number): TempoSegment {
  return spec.segments.reduce(
    (found, segment) => (segment.atMs <= timeMs ? segment : found),
    spec.segments[0],
  );
}

/** The clock a spec describes. Throws {@link GradingConfigError} on an invalid spec. */
export function clockFromSpec(spec: ClockSpec): Clock {
  const errors = clockSpecErrors(spec);
  if (errors.length > 0) {
    throw new GradingConfigError(errors.join('; '));
  }
  return {
    msAtBeat: (beat: number) => {
      const segment = segmentForBeat(spec, beat);
      const msPerBeat = MS_PER_MINUTE / segment.bpm;
      return segment.atMs + (beat - segment.atBeat) * msPerBeat;
    },
    msPerBeatAt: (beat: number) => MS_PER_MINUTE / segmentForBeat(spec, beat).bpm,
  };
}

/** The beat the clock has reached at a session time. */
export function beatAtMs(spec: ClockSpec, timeMs: number): number {
  const segment = segmentForTime(spec, timeMs);
  return segment.atBeat + (timeMs - segment.atMs) / (MS_PER_MINUTE / segment.bpm);
}

/**
 * The map after a tempo change at `atMs`: a new segment anchored where the clock is at that
 * moment. A change at the very moment the last segment began replaces that segment's tempo.
 */
export function withTempoChange(spec: ClockSpec, atMs: number, bpm: number): ClockSpec {
  if (!isTempo(bpm)) {
    throw new GradingConfigError(`tempo must be a positive number of beats per minute, got ${bpm}`);
  }
  const last = spec.segments[spec.segments.length - 1];
  if (atMs < last.atMs) {
    throw new GradingConfigError(`a tempo change must not come before the last one (${atMs})`);
  }
  if (atMs === last.atMs) {
    return { ...spec, segments: [...spec.segments.slice(0, -1), { ...last, bpm }] };
  }
  return {
    ...spec,
    segments: [...spec.segments, { atMs, atBeat: beatAtMs(spec, atMs), bpm }],
  };
}

export function slowestBpm(spec: ClockSpec): number {
  return Math.min(...spec.segments.map((segment) => segment.bpm));
}

export function fastestBpm(spec: ClockSpec): number {
  return Math.max(...spec.segments.map((segment) => segment.bpm));
}
